import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Inject, Input, OnInit, Optional, QueryList, SkipSelf, ViewChild, ViewChildren } from '@angular/core';
import {
  FormControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { MatTabGroup } from '@angular/material/tabs';
import { Namespace, SendOnBehalfOf } from 'enums-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { ISignatureConfig } from 'interfaces-lib';
import { IRecipientInfo } from 'recipient-info-service-lib';
import { UtilsService } from 'utils-service-lib';
import { Adv2ConfigComponent } from '../../adv2-config/adv2-config.component';
import { WorkflowConfigComponent } from '../config/workflow-config.component';
import { DocusignTabService, IDocusignConfig, SettingType } from './docusign-tab.service';

@Component({
  selector: 'app-docusign-tab',
  templateUrl: './docusign-tab.component.html',
  styleUrls: ['../config/workflow-config.component.scss', './docusign-tab.component.scss'],
  animations: [
    trigger('transitionNewItemInOut', [
      transition(':enter', [
        style({ minHeight: '0', height: '0' }),
        animate('.05s ease-out', style({ minHeight: '38px', height: '38px' })),
      ]),
      transition(':leave', [
        style({ minHeight: '38px', height: '38px' }),
        animate('.05s ease-out', style({ minHeight: '0', height: '0' })),
      ]),
    ]),
    trigger('transitionNewRecipientInOut', [
      transition(':enter', [style({ height: '0' }), animate('.4s ease-out', style({ height: '218px' }))]),
      transition(':leave', [style({ height: '218px' }), animate('.4s ease-out', style({ height: '0' }))]),
    ]),
  ],
  standalone: false,
})
export class DocusignTabComponent implements OnInit {
  private static createRecipientGroup(recipient: IRecipientInfo): UntypedFormGroup {
    return new UntypedFormGroup({
      name: new UntypedFormControl(recipient.name, Validators.required),
      email: new UntypedFormControl(recipient.email),
      role: new UntypedFormControl(recipient.role, DocusignTabComponent.roleValidator()),
      editing: new UntypedFormControl({ value: false, disabled: true }),
    });
  }

  //#endregion

  static signatureEnabled: boolean;
  readonly SendingOnBehalfOf = SendOnBehalfOf;

  @Input() namespace;
  @Input() formGroup: UntypedFormGroup;
  @Input() isDocMerge: boolean;
  @Input() parent: WorkflowConfigComponent;

  @ViewChildren('recipientEmailInput') recipientEmailInputs: QueryList<ElementRef>;
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChild('sendingOnBehalfOf') sendingOnBehalfOfInput: ElementRef;

  panel = 0;

  addPerson: boolean;
  addEntity: boolean;

  config: IDocusignConfig = {
    loadingSuccess: true,
    accounts: {
      defaultAccount: { accountId: null, accountName: null },
      list: [],
      loading: true,
      toggleValue: SettingType.Default,
      isValid: true,
    },
    brands: {
      list: [],
      accountBrandsMap: {},
      loading: true,
      toggleValue: SettingType.Default,
      isValid: true,
    },
    templates: {
      list: [],
      accountTemplatesMap: {},
      currentTemplateRoles: [],
      loading: true,
      toggleValue: SettingType.Default,
      isValid: true,
    },
  };

  readonly directOutputFileSaveEnabled = this.featureFlagService.enabled(FeatureFlagService.FLAGS.directOutputFileSave);

  constructor(
    private dialogRef: MatDialogRef<DocusignTabComponent>,
    private dialog: MatDialog,
    private docusignTabService: DocusignTabService,
    private featureFlagService: FeatureFlagService,
    @Optional() @SkipSelf() @Inject(MatTabGroup) private parentTabGroup: MatTabGroup,
  ) {}

  static buildForm(signatureConfig?: any, combinedOutputDefault = false): UntypedFormGroup {
    const recipientsForm = new UntypedFormArray([]);

    if (signatureConfig) {
      DocusignTabComponent.signatureEnabled = signatureConfig.enabled;

      if (signatureConfig.recipients) {
        const recipients = signatureConfig.recipients;

        recipients.forEach((r) => {
          recipientsForm.push(DocusignTabComponent.createRecipientGroup(r));
        });

        return new UntypedFormGroup({
          provider: new UntypedFormControl('docusign'),
          enabled: new UntypedFormControl(signatureConfig.enabled),
          properties: new UntypedFormGroup({
            signingOrder: new UntypedFormControl(signatureConfig.properties?.signingOrder),
            ccFollowsSigningOrder: new UntypedFormControl(signatureConfig.properties?.ccFollowsSigningOrder),
            signedOutputFileType: new UntypedFormControl(signatureConfig.properties?.signedOutputFileType ?? true),
            combinedOutput: new UntypedFormControl(signatureConfig.properties?.combinedOutput ?? combinedOutputDefault),
            directOutputFileSave: new UntypedFormControl(signatureConfig.properties?.directOutputFileSave ?? true),
            sendingOnBehalfOfOptions: new UntypedFormGroup({
              type: new UntypedFormControl(signatureConfig.properties?.sendingOnBehalfOfOptions?.type ?? SendOnBehalfOf.KAD_ACCOUNT),
              email: new UntypedFormControl(
                signatureConfig.properties?.sendingOnBehalfOfOptions?.email,
                signatureConfig.properties?.sendingOnBehalfOfOptions?.type === SendOnBehalfOf.SPECIFIC_USER &&
                  signatureConfig.properties?.sendingOnBehalfOfOptions?.email
                  ? UtilsService.emailValidator()
                  : null,
              ),
            }),
            alternativeAccount: new UntypedFormControl(signatureConfig.properties?.alternativeAccount),
            brandId: new UntypedFormControl(signatureConfig.properties?.brandId),
            templateId: new UntypedFormControl(signatureConfig.properties?.templateId),
            documentPosition: new UntypedFormControl(signatureConfig.properties?.documentPosition),
          }),
          recipients: recipientsForm,
          documents: new UntypedFormArray([new UntypedFormControl({})]),
          emailSubject: new UntypedFormControl(signatureConfig.emailSubject),
          emailBody: new UntypedFormControl(signatureConfig.emailBody),
        });
      }
    }

    DocusignTabComponent.signatureEnabled = false;

    return new UntypedFormGroup({
      provider: new UntypedFormControl('docusign'),
      enabled: new UntypedFormControl(false),
      properties: new UntypedFormGroup({
        signingOrder: new UntypedFormControl(true),
        ccFollowsSigningOrder: new UntypedFormControl(true),
        signedOutputFileType: new UntypedFormControl(true),
        combinedOutput: new UntypedFormControl(combinedOutputDefault),
        directOutputFileSave: new UntypedFormControl(true),
        sendingOnBehalfOfOptions: new UntypedFormGroup({
          type: new UntypedFormControl(SendOnBehalfOf.KAD_ACCOUNT),
          email: new UntypedFormControl(),
        }),
        alternativeAccount: new UntypedFormControl(''),
        brandId: new UntypedFormControl(''),
        templateId: new UntypedFormControl(''),
        documentPosition: new UntypedFormControl(''),
      }),
      recipients: recipientsForm,
      documents: new UntypedFormArray([new UntypedFormControl({})]),
      emailSubject: new UntypedFormControl(''),
      emailBody: new UntypedFormControl(''),
    });
  }

  static getFormValue(formGroup: UntypedFormGroup): ISignatureConfig {
    const config = { ...formGroup.value };
    if (config.properties.sendingOnBehalfOfOptions.type !== SendOnBehalfOf.SPECIFIC_USER) {
      config.properties.sendingOnBehalfOfOptions.email = null;
    }
    config.recipients = config.recipients.map((r) => {
      const recipientEmail = r.email ? r.email.trim() : null;
      return { ...r, email: recipientEmail };
    });
    return config;
  }

  static roleValidator(): ValidatorFn {
    return (form: FormControl): ValidationErrors | null => {
      const templateId: string = form?.root?.get('signatureConfig')?.get('properties')?.get('templateId')?.value;

      const isRoleEmpty = !form.value || form.value === '';
      if (isRoleEmpty && templateId) {
        return { isRoleEmpty: true };
      }

      return null;
    };
  }

  //#region Component methods
  ngOnInit(): void {
    this.setupEnabledControl();
    this.setupSendingOnBehalfOptionsFormGroup();
    this.setupEntitySelectedSubscription();
    this.setupControlsHandlers();
    this.docusignTabService.loadUserConfig(this.config, this.formGroup, {
      account: this.alternativeAccountControl,
      brand: this.brandControl,
      template: this.templateControl,
      documentPosition: this.documentPositionControl,
    });
    this.parentTabGroup?.selectedIndexChange.subscribe(() => {
      this.addPerson = false;
      this.addEntity = false;
    });
  }

  openAdv2ConfigComponent() {
    this.dialog.open(Adv2ConfigComponent, {
      minWidth: '50vw',
      minHeight: '20vw',
      maxHeight: '83vh',
      autoFocus: true,
      panelClass: this.namespace + '-namespace',
      data: {
        namespace: this.namespace,
        focusTab: 1,
      },
    });
    this.dialogRef.close();
  }

  handleAccountToggle(event: any) {
    setTimeout(() => {
      if (event.value === SettingType.Default) {
        this.config.accounts.isValid = true; // For "Invalid Entry" case
        this.docusignTabService.clearControls([
          this.alternativeAccountControl,
          this.brandControl,
          this.templateControl,
          this.documentPositionControl,
        ]);
        this.config.brands.list = this.config.brands.accountBrandsMap[this.config.accounts.defaultAccount.accountId] ?? [];
        this.config.templates.list = this.config.templates.accountTemplatesMap[this.config.accounts.defaultAccount.accountId] ?? [];
        this.config.templates.currentTemplateRoles = [];
      }
      this.alternativeAccountControl.updateValueAndValidity();
      this.alternativeAccountControl.markAsDirty();
    }, 0);
  }

  handleBrandToggle(event: any) {
    setTimeout(() => {
      if (event.value === SettingType.Default) {
        this.config.brands.isValid = true; // For "Invalid Entry" case
      }
      this.brandControl.setValue(null);
      this.brandControl.markAsDirty();
    }, 0);
  }

  handleTemplateToggle(event: any) {
    setTimeout(() => {
      if (event.value === SettingType.None) {
        this.config.brands.isValid = true; // For "Invalid Entry" case
        this.config.templates.currentTemplateRoles = [];
        this.docusignTabService.clearRecipientRolesValues(this.formGroup);
      } else if (event.value === SettingType.Custom) {
        this.config.templates.currentTemplateRoles = this.docusignTabService.getTemplateRecipientsRoles(
          this.config,
          this.templateControl.value,
        );
      }
      this.docusignTabService.clearControls([this.templateControl, this.documentPositionControl]);
    }, 0);
  }

  setPanel(index: number) {
    this.panel = index;
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.recipientsFormArray.controls, event.previousIndex, event.currentIndex);
    moveItemInArray(this.recipientsFormArray.value, event.previousIndex, event.currentIndex);
    this.formGroup.markAsDirty();
  }

  removeRecipient(i: number) {
    this.recipientsFormArray.removeAt(i);
    this.formGroup.markAsDirty();
  }

  toggleEditRecipient(i: number): void {
    const editing = this.recipientsFormArray.controls[i].get('editing');
    editing.setValue(!editing.value);
  }

  addRecipient(recipient: IRecipientInfo): void {
    this.recipientsFormArray.push(DocusignTabComponent.createRecipientGroup(recipient));
    this.formGroup.markAsDirty();
    this.addPerson = false;
    this.addEntity = false;
  }

  acceptRecipientEdits(i: number): void {
    this.recipientsFormArray.controls[i].get('editing').setValue(false);
  }

  addPersonButton(): void {
    if (this.addEntity) {
      this.addEntity = false;
      setTimeout((_) => {
        this.addPerson = true;
      }, 800);
    } else {
      this.addPerson = true;
    }
  }

  addEntityButton(): void {
    if (this.addPerson) {
      this.addPerson = false;
      setTimeout((_) => {
        this.addEntity = true;
      }, 800);
    } else {
      this.addEntity = true;
    }
  }

  emailTrim(): void {
    const emailControl = this.sendingOnBehalfOfOptionsFormGroup.get('email');
    const trimmed = emailControl.value?.trim();
    emailControl.setValue(trimmed);
  }

  focusRecipientElement(elements: QueryList<ElementRef>) {
    const recipientInput = elements.last.nativeElement;
    if (recipientInput) {
      recipientInput.focus();
    }
  }

  emailValid(): boolean {
    return this.formGroup.get('emailSubject').valid && this.formGroup.get('emailBody').valid;
  }

  //#endregion

  //#region Private methods
  private updateFormValidators(signatureEnabled: boolean): void {
    const sendForSpecificUser = this.sendForSpecificUserControl.value === SendOnBehalfOf.SPECIFIC_USER;

    if (signatureEnabled) {
      this.docusignTabService.setValidators([
        [this.recipientsFormArray, (control: UntypedFormArray) => this.validateRecipientsAndTemplateId(control)],
        [this.emailSubjectControl, Validators.required],
        [this.emailBodyControl, Validators.required],
        [this.alternativeAccountControl, (control: UntypedFormControl) => this.alternativeAccountValidator(control)],
        [this.brandControl, (control: UntypedFormControl) => this.brandValidator(control)],
        [this.templateControl, (control: UntypedFormControl) => this.templateValidator(control)],
        [this.documentPositionControl, (control: UntypedFormControl) => this.documentPositionValidator(control)],
      ]);

      if (sendForSpecificUser) {
        this.sendOnBehalfOfEmailControl.setValidators(UtilsService.emailValidator());
      } else {
        this.sendOnBehalfOfEmailControl.clearValidators();
      }

      DocusignTabComponent.signatureEnabled = true;
    } else {
      this.docusignTabService.clearValidators([
        this.recipientsFormArray,
        this.emailSubjectControl,
        this.emailBodyControl,
        this.alternativeAccountControl,
        this.brandControl,
        this.sendOnBehalfOfEmailControl,
      ]);
      DocusignTabComponent.signatureEnabled = false;
      this.accordion?.closeAll();
    }
  }

  private setupEnabledControl(): void {
    const enabledControl = this.formGroup.get('enabled');
    this.updateFormValidators(enabledControl.value);
    enabledControl.valueChanges.subscribe((value: boolean) => {
      this.updateFormValidators(value);
    });
  }

  private setupSendingOnBehalfOptionsFormGroup(): void {
    this.sendingOnBehalfOfOptionsFormGroup.get('type').valueChanges.subscribe((value) => {
      this.accordion?.closeAll();

      setTimeout((_) => {
        const emailControl = this.sendingOnBehalfOfOptionsFormGroup.get('email');
        if (value === SendOnBehalfOf.SPECIFIC_USER) {
          emailControl.setValidators(UtilsService.emailValidator());
          this.sendingOnBehalfOfInput.nativeElement.focus();
        } else {
          emailControl.clearValidators();
        }
        emailControl.updateValueAndValidity();
      }, 0);
    });
  }

  private setupEntitySelectedSubscription(): void {
    // this.entityControllerService.entitySelected$.subscribe((entityInfo) => this.addRecipientFromEntity(entityInfo));
  }

  private setupControlsHandlers(): void {
    this.alternativeAccountControl.valueChanges.subscribe(() => {
      if (this.alternativeAccountControl.value) {
        this.config.accounts.isValid = true;
        this.config.brands.list = this.config.brands.accountBrandsMap[this.alternativeAccountControl.value] ?? [];
        this.config.templates.list = this.config.templates.accountTemplatesMap[this.alternativeAccountControl.value] ?? [];
        this.config.templates.currentTemplateRoles = [];
        this.docusignTabService.clearControls([this.brandControl, this.templateControl, this.documentPositionControl]);
      }
    });

    this.brandControl.valueChanges.subscribe(() => {
      if (this.brandControl.value) {
        this.config.brands.isValid = true;
      }
    });

    this.templateControl.valueChanges.subscribe(() => {
      if (this.templateControl.value) {
        this.config.templates.isValid = true;
        this.config.templates.currentTemplateRoles = this.docusignTabService.getTemplateRecipientsRoles(
          this.config,
          this.templateControl.value,
        );
        this.docusignTabService.clearRecipientRolesValues(this.formGroup);
      }
    });

    this.formGroup.get('properties.signedOutputFileType').valueChanges.subscribe((value: boolean) => {
      if (value === true) {
        const fg = this.parent?.signatureOutputFileTypeSectionFormGroup;
        if (!fg) {
          return;
        }
        fg.get('sourceValue').setValue(null);
        const configControl = fg.get('config');
        configControl.setValue({ ...configControl.value, destinations: {} });
        this.parent.signatureDestinationsForOutputFileType?.forEach((d) => (d.documentType = null));
      }
    });

    this.formGroup.get('properties.combinedOutput').valueChanges.subscribe((value: boolean) => {
      if (value === false) {
        this.formGroup.get('properties.signedOutputFileType').setValue(true);
      }
    });
  }

  //#endregion

  //#region Validators
  private alternativeAccountValidator(control: UntypedFormControl) {
    return this.config.accounts.toggleValue === SettingType.Custom && !control.value ? { required: true } : null;
  }

  private brandValidator(control: UntypedFormControl) {
    if (this.config.brands.list?.length && this.config.brands.toggleValue === SettingType.Custom) {
      return control.value ? null : { required: true };
    }
    return null;
  }

  private templateValidator(control: UntypedFormControl) {
    if (this.config.templates.list?.length && this.config.templates.toggleValue === SettingType.Custom) {
      return control.value ? null : { required: true };
    }
    return null;
  }

  private validateRecipientsAndTemplateId(recipients: UntypedFormArray): ValidationErrors | null {
    if (!recipients) {
      return null;
    }

    const properties = this.formGroup.get('properties') as UntypedFormGroup;
    const templateId = properties?.get('templateId')?.value;

    if (recipients.length === 0) {
      return { recipientsRequired: true };
    }

    if (templateId !== null && templateId !== '') {
      const isRecipientMissingRoles = recipients.controls.some((recipient: UntypedFormGroup) => {
        return !recipient.get('role')?.value;
      });

      if (isRecipientMissingRoles) {
        return { recipientRolesRequired: true };
      }
    }

    return null;
  }

  private documentPositionValidator(control: UntypedFormControl) {
    if (this.config.templates.list?.length && this.config.templates.toggleValue === SettingType.Custom) {
      const currentTemplate = this.config.templates.list.find((t) => t.templateId === this.templateControl.value);

      // Required validation
      if (control.value === null || control.value === undefined) {
        return { required: true };
      }

      // Number validation
      if (
        isNaN(control.value) ||
        control.value < 0 ||
        control.value > currentTemplate.documents.length + 1 ||
        !Number.isInteger(control.value)
      ) {
        return { invalid: true };
      }
    }
    return null;
  }

  //#endregion

  //#region Getters
  get emailSubjectControl(): UntypedFormControl {
    return this.formGroup.get('emailSubject') as UntypedFormControl;
  }

  get emailBodyControl(): UntypedFormControl {
    return this.formGroup.get('emailBody') as UntypedFormControl;
  }

  get propertiesFormGroup(): UntypedFormGroup {
    return this.formGroup.get('properties') as UntypedFormGroup;
  }

  get sendingOnBehalfOfOptionsFormGroup(): UntypedFormGroup {
    return this.propertiesFormGroup.get('sendingOnBehalfOfOptions') as UntypedFormGroup;
  }

  get sendOnBehalfOfEmailControl(): UntypedFormControl {
    return this.sendingOnBehalfOfOptionsFormGroup.get('email') as UntypedFormControl;
  }

  get sendForSpecificUserControl(): UntypedFormControl {
    return this.sendingOnBehalfOfOptionsFormGroup.get('type') as UntypedFormControl;
  }

  get recipientsFormArray(): UntypedFormArray {
    return this.formGroup.get('recipients') as UntypedFormArray;
  }

  get alternativeAccountControl(): UntypedFormControl {
    return this.propertiesFormGroup.get('alternativeAccount') as UntypedFormControl;
  }

  get brandControl(): UntypedFormControl {
    return this.propertiesFormGroup.get('brandId') as UntypedFormControl;
  }

  get templateControl(): UntypedFormControl {
    return this.propertiesFormGroup.get('templateId') as UntypedFormControl;
  }

  get documentPositionControl(): UntypedFormControl {
    return this.propertiesFormGroup.get('documentPosition') as UntypedFormControl;
  }

  get signatureEnabled(): boolean {
    return DocusignTabComponent.signatureEnabled;
  }

  get selectedAccountName() {
    return this.docusignTabService.getSelectedAccountName(this.config, this.alternativeAccountControl);
  }

  get selectedBrandName() {
    return this.docusignTabService.getSelectedBrandName(this.config, this.brandControl);
  }

  get selectedTemplateName() {
    return this.docusignTabService.getSelectedTemplateName(this.config, this.templateControl);
  }

  get signedOutputFileType() {
    return this.formGroup.get('properties.signedOutputFileType').value;
  }

  get combinedOutput() {
    return this.formGroup.get('properties.combinedOutput').value;
  }

  get showSignedOutputFileType() {
    return this.namespace !== Namespace.salesforce;
  }
}
